/**
 * Payment bulk-search descriptor — adapts SearchPayments to BulkSearchResults.
 * The queried ids go in as paymentIdentifiers; status/state are server-side
 * PropertyFilter refinements.
 */
import Badge from '@cloudscape-design/components/badge';
import CopyToClipboard from '@cloudscape-design/components/copy-to-clipboard';
import { PropertyFilterProps } from '@cloudscape-design/components/property-filter';
import SpaceBetween from '@cloudscape-design/components/space-between';
import { titleCase, fullTimestamp } from 'src/store/slices/paymentDetail/paymentDetailFormat';
import { ApiArtifactRef } from 'src/store/slices/paymentDetail/paymentDetailApi';
import { DetailLink } from 'src/components/common/DetailLink';
import {
  ApiPaymentSummary,
  PaymentFilters,
  SearchPaymentsInput,
} from 'src/store/slices/searchPayments/searchPaymentsApi';
import {
  searchPayments, searchPaymentsRegion, clearSearchPayments,
} from 'src/store/slices/searchPayments/searchPaymentsSlice';
import {
  BulkSearchArgs,
  BulkSearchDescriptor,
  StatusCell,
  dash,
  firstOf,
  joinList,
  refTypeColumns,
} from './bulkSearchShared';

const FILTERING_PROPERTIES: PropertyFilterProps.FilteringProperty[] = [
  { key: 'status', propertyLabel: 'Status', groupValuesLabel: 'Status values', operators: ['='] },
  { key: 'state', propertyLabel: 'State', groupValuesLabel: 'State values', operators: ['='] },
];

/** Columns shown by default; every other column stays toggleable in Preferences. */
const DEFAULT_VISIBLE = ['id', 'beaconStatus', 'paymentStatus', 'transmission', 'response', 'updatedAt'];

const buildInput = ({ ids, pageNumber, pageSize, filters }: BulkSearchArgs): SearchPaymentsInput => {
  const f: PaymentFilters = { paymentIdentifiers: ids };
  const status = firstOf(filters, 'status');
  const state = firstOf(filters, 'state');
  if (status) f.beaconPaymentStatus = status;
  if (state) f.beaconPaymentState = state;
  return { filters: f, pagination: { pageNumber, pageSize } };
};

/** Show the id intact up to 120 chars; longer ones middle-truncate (full value stays in the tooltip + copy). */
const shortId = (id: string): string => (id.length <= 120 ? id : `${id.slice(0, 60)}…${id.slice(-59)}`);

/**
 * A payment's artifacts for one direction, rendered as the artifact's own type
 * badge + a middle-truncated id (full value on hover) + an icon copy button
 * (copies the full id). The type label and id are taken verbatim from each
 * artifact in the response — nothing is hard-coded. OUTBOUND is the transmission
 * sent to the bank; INBOUND are the responses. Direction is the universal
 * discriminator, so this covers both file-based and API-based payments.
 */
const ArtifactRefCell = (
  { artifacts, direction }: { artifacts?: ApiArtifactRef[]; direction: 'OUTBOUND' | 'INBOUND' },
) => {
  const refs = (artifacts ?? []).filter((a) => (a.direction ?? '').toUpperCase() === direction);
  if (refs.length === 0) return <>-</>;
  return (
    <SpaceBetween size="xxs">
      {refs.map((a, i) => (
        <div key={`${a.artifactId}-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <CopyToClipboard
            variant="icon"
            textToCopy={a.artifactId}
            copyButtonAriaLabel={`Copy ${a.artifactId}`}
            copySuccessText="Copied"
            copyErrorText="Failed"
          />
          {a.artifactType ? <Badge>{a.artifactType}</Badge> : null}
          <span title={a.artifactId} style={{ fontFamily: 'monospace', fontSize: 12 }}>
            {shortId(a.artifactId)}
          </span>
        </div>
      ))}
    </SpaceBetween>
  );
};

/** A formatted timestamp led by a copy button that copies the displayed value. */
const TimestampCell = ({ iso }: { iso?: string }) => {
  if (!iso) return <>-</>;
  const ts = fullTimestamp(iso);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      <CopyToClipboard
        variant="icon"
        textToCopy={ts}
        copyButtonAriaLabel={`Copy ${ts}`}
        copySuccessText="Copied"
        copyErrorText="Failed"
      />
      <span>{ts}</span>
    </div>
  );
};

export const paymentSearchDescriptor: BulkSearchDescriptor<ApiPaymentSummary> = {
  entity: 'payment',
  title: 'Bulk payment search',
  path: '/payments',
  bulkPlaceholder: 'Enter multiple payment, artifact or execution IDs, one per line',
  filteringProperties: FILTERING_PROPERTIES,
  columns: [
    {
      id: 'id',
      header: 'End-to-end id',
      cell: (p) => <DetailLink to={`/payment?id=${encodeURIComponent(p.id)}`} newTab>{p.id}</DetailLink>,
    },
    { id: 'beaconStatus', header: 'Beacon status', cell: (p) => <StatusCell state={p.beaconPaymentState} label={p.beaconPaymentStatus} /> },
    { id: 'beaconState', header: 'Beacon state', cell: (p) => dash(p.beaconPaymentState) },
    { id: 'paymentStatus', header: 'Payment status', cell: (p) => dash(p.paymentDetails?.paymentStatus) },
    // Transmission = the OUTBOUND artifact (its id is the transmission identifier, type e.g. PAIN001).
    { id: 'transmission', header: 'Transmission', cell: (p) => <ArtifactRefCell artifacts={p.artifacts} direction="OUTBOUND" /> },
    // Response = the INBOUND artifact(s) (response id + response / inbound artifact type).
    { id: 'response', header: 'Response', cell: (p) => <ArtifactRefCell artifacts={p.artifacts} direction="INBOUND" /> },
    { id: 'paymentType', header: 'Payment type', cell: (p) => dash(p.paymentDetails?.paymentType) },
    { id: 'paymentMethod', header: 'Payment method', cell: (p) => dash(p.paymentDetails?.paymentMethod) },
    { id: 'bank', header: 'Bank', cell: (p) => dash(p.paymentDetails?.bank) },
    { id: 'currency', header: 'Currency', cell: (p) => dash(p.paymentDetails?.currency) },
    { id: 'pluginName', header: 'Plugin', cell: (p) => dash(p.paymentDetails?.pluginName) },
    { id: 'paymentSourceId', header: 'Payment source id', cell: (p) => dash(p.paymentDetails?.paymentSourceId) },
    { id: 'system', header: 'System', cell: (p) => dash(p.currentSystem) },
    { id: 'direction', header: 'Direction', cell: (p) => (p.currentDirection ? titleCase(p.currentDirection) : '-') },
    { id: 'systemsInvolved', header: 'Systems involved', cell: (p) => joinList(p.systemsInvolved) },
    { id: 'amazonInternalSignature', header: 'Amazon internal signature', cell: (p) => dash(p.amazonInternalSignature) },
    { id: 'disbursementId', header: 'Disbursement id', cell: (p) => dash(p.disbursementId) },
    { id: 'reissueOf', header: 'Reissue of', cell: (p) => dash(p.reissueDetails?.reissueOf) },
    { id: 'createdAt', header: 'Created', cell: (p) => (p.createdAt ? fullTimestamp(p.createdAt) : '-') },
    { id: 'updatedAt', header: 'Updated Time Stamp', cell: (p) => <TimestampCell iso={p.updatedAt} /> },
  ],
  defaultVisibleColumns: DEFAULT_VISIBLE,
  // One column per artifact ref type (PAIN001, CAMT053, …) — header = type, cell = artifactId.
  dynamicColumns: (items) => refTypeColumns(items, (p) => (p.artifacts ?? []).map((a) => ({ type: a.artifactType, id: a.artifactId }))),
  run: (dispatch, args) => { dispatch(searchPayments({ input: buildInput(args) })); },
  runRegion: (dispatch, { region, ...args }) => {
    dispatch(searchPaymentsRegion({ input: buildInput(args), region }));
  },
  clear: (dispatch) => { dispatch(clearSearchPayments()); },
  select: (s) => ({
    items: s.searchPayments.payments,
    total: s.searchPayments.total,
    loading: s.searchPayments.loading,
    error: s.searchPayments.error,
    regionResults: s.searchPayments.regionResults,
    searchedRegions: s.searchPayments.searchedRegions,
    regionErrors: s.searchPayments.regionErrors,
    loadingRegions: s.searchPayments.loadingRegions,
  }),
};
